<div class="tlm-dialog">

  <!-- ── Header ── -->
  <div class="tlm-header">
    <div class="tlm-header-left">
      <h2 class="tlm-title">{{ data.mode === 'templates' ? 'Tasks' : 'Tasks' }}</h2>
      <span class="tlm-status-chip" [ngClass]="'chip--' + data.statusKey">{{ data.title }}</span>
      <span class="tlm-count-badge">{{ filteredItems.length }}</span>
    </div>
    <div class="tlm-header-right">
      @if (data.showBulkActions) {
        <label class="tlm-bulk-merge">
          <input type="checkbox" (change)="onMergeCheck($event)">
          Merge PDF
        </label>
        <button type="button"
          class="tlm-bulk-btn"
          [disabled]="!hasSelected || isDownloadingPdf || isDownloadingMedia"
          (click)="downloadPdf()"
          [matTooltip]="isDownloadingPdf ? 'Preparing PDF…' : (mergePdfChecked ? 'Download selected as one merged PDF' : 'Download selected as separate PDFs')">
          @if (isDownloadingPdf) {
            <mat-spinner diameter="14" class="tlm-bulk-spinner"></mat-spinner>
            <span>Preparing…</span>
          } @else {
            @if ((mergePdfChecked ? 'merge_type' : 'picture_as_pdf') | brandIcon; as brandSvg) { <mat-icon class="tlm-bulk-icon" [svgIcon]="brandSvg"></mat-icon> } @else { <mat-icon class="tlm-bulk-icon">{{ mergePdfChecked ? 'merge_type' : 'picture_as_pdf' }}</mat-icon> }
            <span>PDF {{ mergePdfChecked ? '(merged)' : '' }}</span>
          }
        </button>
        <button type="button"
          class="tlm-bulk-btn"
          [disabled]="!hasSelected || isDownloadingPdf || isDownloadingMedia"
          (click)="downloadMedia()"
          [matTooltip]="isDownloadingMedia ? 'Preparing media…' : 'Download selected media'">
          @if (isDownloadingMedia) {
            <mat-spinner diameter="14" class="tlm-bulk-spinner"></mat-spinner>
            <span>Preparing…</span>
          } @else {
            <mat-icon class="tlm-bulk-icon" svgIcon="c:cloud-download"></mat-icon>
            <span>Download</span>
          }
        </button>
      }
      <button type="button" class="tlm-close-btn" (click)="close()" aria-label="Close">
        <mat-icon svgIcon="c:x"></mat-icon>
      </button>
    </div>
  </div>

  <!-- ── Search bar ── -->
  <div class="tlm-search-bar">
    <mat-icon class="tlm-search-icon" svgIcon="c:search"></mat-icon>
    <input
      class="tlm-search-input"
      type="text"
      [placeholder]="data.searchPlaceholder || 'Search by title or template…'"
      [(ngModel)]="searchTerm"
      autocomplete="off" />
    @if (searchTerm) {
      <button class="tlm-search-clear" (click)="searchTerm = ''" matTooltip="Clear">
        <mat-icon svgIcon="c:x"></mat-icon>
      </button>
    }
  </div>

  <!-- ── Select-all bar (tasks mode + bulk actions) ── -->
  @if (data.showBulkActions && filteredItems.length > 0) {
    <div class="tlm-select-all-bar">
      <button type="button" class="tlm-select-all-control"
        (click)="toggleSelectAll(!allSelected)">
        <span class="tlm-checkmark"
          [class.tlm-checkmark--checked]="allSelected"
          [class.tlm-checkmark--indeterminate]="someSelected">
          @if (allSelected) {
            <mat-icon class="tlm-checkmark-icon" svgIcon="c:check"></mat-icon>
          } @else if (someSelected) {
            <mat-icon class="tlm-checkmark-icon" svgIcon="c:minus"></mat-icon>
          }
        </span>
        <span class="tlm-select-all-label">
          @if (hasSelected) {
            <strong>{{ selectedCount }}</strong> of {{ data.items.length }} selected
          } @else {
            Select all
          }
        </span>
      </button>
      @if (hasSelected) {
        <button class="tlm-clear-link" (click)="toggleSelectAll(false)">Clear</button>
      }
    </div>
  }

  <!-- ── List ── -->
  <div class="tlm-list">
    @if (filteredItems.length === 0) {
      <div class="tlm-empty">
        <mat-icon class="tlm-empty-icon" svgIcon="c:search-slash"></mat-icon>
        <p>{{ searchTerm ? 'No matches found.' : 'No records found.' }}</p>
      </div>
    }

    @for (item of filteredItems; track item) {
      @if (data.mode === 'templates') {
        <div class="flex flex-col gap-0.5">
          <div class="tlm-template-card" (click)="onItemClick(item)" tabindex="0"
            [class.tlm-template-card--required]="data.showMandatory && item.isMandatory || itemErrors[item.id]"
            [class.opacity-60]="pendingItemId === item.id"
            [class.pointer-events-none]="pendingItemId !== null"
            (keydown.enter)="onItemClick(item)">
            <div class="tlm-template-icon">
              <mat-icon svgIcon="c:clipboard"></mat-icon>
            </div>
            <div class="tlm-template-info">
              <h4 class="tlm-template-name" [title]="item.name">{{ item.name }}</h4>
              @if (data.showMandatory && item.isMandatory) {
                <span class="tlm-required-badge">Required</span>
              } @else {
                <span class="tlm-template-hint">Select to start task</span>
              }
            </div>
            <button mat-flat-button color="primary"
              class="tlm-start-btn"
              [disabled]="pendingItemId !== null"
              (click)="$event.stopPropagation(); onItemClick(item)">
              {{ pendingItemId === item.id ? '…' : (data.actionLabel || 'Start') }}
            </button>
          </div>
          @if (itemErrors[item.id]) {
            <p class="text-[11px] text-red-500 px-1 leading-tight">{{ itemErrors[item.id] }}</p>
          }
        </div>
      } @else {
        <!-- eslint-disable-next-line @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -- pre-existing pattern; keyboard accessibility tracked under COL-37 -->
        <div class="tlm-card cursor-pointer"
          [class.tlm-card--selected]="!!item.selected"
          (click)="onItemClick(item)">

          @if (data.showBulkActions) {
            <button type="button"
              class="tlm-checkmark"
              [class.tlm-checkmark--checked]="!!item.selected"
              (click)="$event.stopPropagation(); onCheckboxToggle(item, !item.selected)"
              [attr.aria-label]="item.selected ? 'Deselect' : 'Select'">
              @if (item.selected) {
                <mat-icon class="tlm-checkmark-icon" svgIcon="c:check"></mat-icon>
              }
            </button>
          }

          <div class="tlm-card-body">
            <!-- Title -->
            <div class="flex items-center gap-1.5">
              <h4 class="tlm-card-title" [title]="item.title">
                {{ item.title || 'Untitled' }}
              </h4>
              @if (data.isRequiredTask?.(item)) {
                <span class="tlm-required-badge">Required</span>
              }
            </div>

            <!-- Template name + status badge -->
            <div class="tlm-card-template-row">
              <p class="tlm-card-template">{{ item.templateName }}</p>
              <span class="tlm-status-badge"
                [ngClass]="data.getStatusBadgeClass!(item.status)">
                {{ item.status }}
              </span>
            </div>

            <!-- Footer: user + updated (dashboard card style) -->
            <div class="tlm-card-footer">
              <div class="tlm-user-row">
                <div class="tlm-avatar">{{ data.getInitials!(item.createdByName || 'U') }}</div>
                <span class="tlm-user-name">{{ item.createdByName || 'Unknown' }}</span>
              </div>
              <span class="tlm-updated">
                Updated {{ data.getLocalDate!(item.modifiedDate ?? item.createdDate) }}
              </span>
            </div>
          </div>

          <!-- Action buttons -->
          <div class="flex items-center gap-2 shrink-0 pl-2">
            @if (item.status === 'Completed') {
              <button mat-stroked-button class="!text-xs !h-7 !min-w-0 !px-3 !rounded-lg"
                (click)="$event.stopPropagation(); onItemClick(item)">
                View Report
              </button>
            } @else {
              <button mat-stroked-button class="!text-xs !h-7 !min-w-0 !px-3 !rounded-lg"
                (click)="$event.stopPropagation(); onItemClick(item)">
                Continue
              </button>
            }
            @if (data.onEdit || data.onViewReport) {
              <button mat-icon-button class="!w-7 !h-7 !leading-7" (click)="$event.stopPropagation()" [matMenuTriggerFor]="tlmItemMenu">
                <mat-icon class="!text-[18px] !w-[18px] !h-[18px] text-[#4c4c4c]" svgIcon="c:dots-vertical"></mat-icon>
              </button>
              <mat-menu #tlmItemMenu="matMenu">
                <button mat-menu-item (click)="onEdit(item)">
                  <mat-icon class="!text-primary" svgIcon="c:edit"></mat-icon>
                  <span>Edit</span>
                </button>
                <button mat-menu-item (click)="onViewReport(item)">
                  <mat-icon class="!text-primary" svgIcon="c:file-text"></mat-icon>
                  <span>View Report</span>
                </button>
              </mat-menu>
            }
          </div>

        </div>
      }
    }
  </div>

</div>
